import activeWindows from '@/store/modules/active-windows';
import store from '@/store';
import dialogInstancePropertyCreator
    from "@/components/global_components/dialogs/global-dialogs/dialogInstancePropertyCreator";
import WebSocketDialog from "@/components/global_components/dialogs/global-dialogs/WebSocketDialog";
import WorkAuthentication from "@/services/authentication_services/WorkAuthentication";
import WorkCookies from "@/services/cookie_services/WorkCookies";
import SNACKBAR_TYPES from "@/utils/enum/SNACKBAR_TYPES";
import axios from "axios";
import i18n from '@/language/i18n';
import { EventBus } from '@/event-bus/event-bus';

function parseMessage(data) {
    try {
        return JSON.parse(data);
    } catch (e) {
        return null;
    }
}
    
export default {
    ws: null,
    wsStatus: 'CLOSED',
    reconnectAttempts: 0,
    maxReconnectAttempts: Infinity,
    reconnectDelay: 1000,
    reconnectTimeoutId: null,

    async createConnectionToBackend(token, system_config_uri = '/rest/v4/configurations/system') {
        const config = await axios.get(system_config_uri);
        if (!config?.data?.data?.websocketEndpoint) {
            console.log('websocket endpoint config unavailable');
            return;
        }
        const wsPath = config.data.data.websocketEndpoint;
        console.log(`connecting to websocket endpoint at ${wsPath}`);

        this.closeConnection();

        this.connectWebSocket(wsPath, token);
    },

    connectWebSocket(url, token) {
        try {
            this.ws = new WebSocket(url);
            this.wsStatus = 'CONNECTING';

            this.ws.onopen = () => {
                this.wsStatus = 'OPEN';
                this.reconnectAttempts = 0;
                
                this.send(JSON.stringify({
                    type: 'registerRequest',
                    data: { token },
                }));
            };

            this.ws.onmessage = (event) => {
                this.handleSocketMessage(event.data);
            };

            this.ws.onclose = (event) => {
                console.log('WebSocket connection closed', event);
                this.wsStatus = 'CLOSED';
                this.handleSocketClose(event);
                this.attemptReconnect(url, token);
            };

            this.ws.onerror = (event) => {
                console.log('WebSocket error occurred', event);
                this.wsStatus = 'ERROR';
                this.handleSocketError(event);
            };

        } catch (error) {
            console.error('Failed to create WebSocket connection:', error);
            this.wsStatus = 'ERROR';
        }
    },

    attemptReconnect(url, token) {
        if (this.reconnectAttempts < this.maxReconnectAttempts) {
            this.reconnectAttempts++;
            console.log(`Attempting to reconnect (${this.reconnectAttempts}/${this.maxReconnectAttempts})...`);
            
            this.reconnectTimeoutId = setTimeout(() => {
                this.connectWebSocket(url, token);
            }, this.reconnectDelay);
        } else {
            console.log('WebSocket reconnection failed - max attempts reached');
            this.wsStatus = 'FAILED';
        }
    },

    send(data) {
        if (this.ws && this.ws.readyState === WebSocket.OPEN) {
            this.ws.send(data);
        } else {
            console.warn('WebSocket is not open. ReadyState:', this.ws?.readyState);
        }
    },

    closeConnection() {
        if (this.reconnectTimeoutId) {
            clearTimeout(this.reconnectTimeoutId);
            this.reconnectTimeoutId = null;
        }
        
        if (this.ws) {
            this.ws.close();
            this.ws = null;
        }
        this.wsStatus = 'CLOSED';
    },

    closeConnectionToBackend() {
        this.closeConnection();
    },

    getStatus() {
        return this.wsStatus;
    },

    isConnected() {
        return this.wsStatus === 'OPEN' && this.ws && this.ws.readyState === WebSocket.OPEN;
    },

    handleSuccess() {
        return store.dispatch('globalSnackbarStore/showSnackbarWithMessage', {
            type: SNACKBAR_TYPES.INFO,
            message: i18n.t('Messages.sentences.mobileDeviceDataTransmissionSuccess')
        });
    },

    async handleSocketMessage(message) {
        const messageAsJson = parseMessage(message);
        if (!messageAsJson) return;

        var type = messageAsJson.type;

        if (messageAsJson?.data?.taskId) {
            try {
                let task = await store.dispatch('mobileWrapperTasks/getAndRemoveTask', messageAsJson.data.taskId);
                task.callback(messageAsJson.data?.value, task?.taskPayload);
                this.handleSuccess();
            } catch (e) {
                // Handle error
            }
        }
        else if (this.messageHandler.hasOwnProperty(type)) {
            this.messageHandler[type](messageAsJson.data);
        } else {
            this.defaultHandler(messageAsJson.data);
        }
    },

    handleSocketClose(event) {
        if (event.reason == "PUSH_CLOSE" && WorkAuthentication.isAuthenticated()) {
            WorkAuthentication.logout();
        }
        store.commit('updateListeners/setWebsocketUpdateRequired', true);
        WorkCookies.setWebsocket(false);
    },

    handleSocketError(event) {
        WorkCookies.setWebsocket(false);
        console.log('WebSocket error:', event);
        store.commit('updateListeners/setWebsocketUpdateRequired', true);
    },

    defaultHandler: function (message) {
        window.console.log("unhandled websocket message from backend received: " + message);
    },

    messageHandler: {
        serverToClientPing: function (message) {
            window.console.log("PingHandler: " + JSON.stringify(message));
            const webSocketDialog = dialogInstancePropertyCreator(WebSocketDialog);
            webSocketDialog.open();
        },
        
        registerResponse: function (message) {
            window.console.log("websocket connection successful");
            if (message?.workClientConnected != undefined)
                WorkCookies.setWebsocket(message.workClientConnected);
            if (store.hasModule('websocket')) {
                store.commit('websocket/setSessionId', message.sessionId);
            }
            store.commit('updateListeners/setWebsocketUpdateRequired', false);
            store.commit('errorNotificationStore/resetError', { fieldName: 'Websocketverbindung' });
        },

        workClientConnectionMessage: function (message) {
            WorkCookies.setWebsocket(message.clientConnected);

            EventBus.$emit('workClientConnectionChanged', {
                field: 'workClientConnected',
                value: message.clientConnected
            });
        },

        clientWindowModuleMessage: function (message) {
            if (message.error) {
                if (message.sessionId === store.getters['websocket/getSessionId'] && !['projectWorkflow', 'projects'].includes(message.windowType)) {
                    return;
                }
                
                EventBus.$emit('windowModuleUpdate', {
                    type: message.windowType,
                    id: message.windowId,
                    field: 'pushNotificationError',
                    value: message.error
                });
            }
            else if (message.warning) {
                EventBus.$emit('windowModuleUpdate', {
                    type: message.windowType,
                    id: message.windowId,
                    field: 'pushNotificationWarning',
                    value: message.warning
                });
            }    
            else {
                if (!WorkAuthentication.isAuthenticatedForExtern()) {
                    activeWindows.setWindowDataField({
                        type: message.windowType,
                        id: message.windowId,
                        targetFieldPath: `transientData.${message.field}`,
                        value: message.value
                    });
                }
                
                EventBus.$emit('windowModuleUpdate', {
                    type: message.windowType,
                    id: message.windowId,
                    field: message.field,
                    value: message.value
                });
            }
        },

        clientUnreadNotificationTrackerMessage: function (message) {
            store.commit('badgeCounter/setUnreadNotificationsCount', message.unreadNotifications);
        },

        clientWindowCountMessage: function (message) {
            store.commit('badgeCounter/setActiveWindowsCount', message.count);
        },

        clientConnectedDevicesChangedMessage: function (message) {
            window.console.log("client connected devices changed message received: " + JSON.stringify(message));
            store.commit('updateListeners/setProfilUpdateRequired', true);
        },

        updateSystemInformationMessage: function (message) {
            store.dispatch("configuration/loadConfiguration", {url:'/rest/v4/system/information?page=unpaged&sort=viewOrder'});   
            store.commit('updateListeners/setMaintenanceModeUpdateRequired', true);
        },
    },
};
